/**
 * Resolves a resource path to the section it addresses in a parsed `.tres`: the one
 * sub-resource lookup every loader of a path shares, with one error policy. A slice
 * decodes the section it returns.
 */
import { perObject } from '../utils/perObject.js';
import { findSubResource } from './SubResourceResolver.js';
import { parseSubResourcePath } from './subResourcePath.js';
/** The section `path` addresses inside `file`, or undefined for an id `file` does not declare. */
function findResourceSection(file, path) {
    const { subResourceId } = parseSubResourcePath(path);
    if (subResourceId === undefined)
        return { type: file.resourceType, properties: file.properties };
    const sub = findSubResource(file.subResources, subResourceId);
    return sub && { type: sub.type, properties: sub.data };
}
/** The section `path` addresses in `file` as a resource whose id is `path`, or undefined for an undeclared id. */
export function findSectionResource(file, path) {
    const section = findResourceSection(file, path);
    return section && { id: path, type: section.type, data: section.properties };
}
/**
 * Written only by `scopedSection`: the scoped sections of each parsed file by path. A parse drops
 * its entries when it goes, and a reload parses a new object, so an entry never outlives its file.
 */
const scopedSections = perObject(() => new Map());
/**
 * {@link findSectionResource}, scoped to the tables of `file` that its references resolve in. One
 * parse answers a path with one object, so a content key over the section is read once.
 */
export function scopedSection(file, path) {
    const byPath = scopedSections(file);
    if (!byPath.has(path))
        byPath.set(path, buildScopedSection(file, path));
    return byPath.get(path);
}
function buildScopedSection(file, path) {
    const resource = findSectionResource(file, path);
    if (!resource)
        return undefined;
    return { resource, resources: fileResources(file) };
}
/** The tables of `file` that its references resolve in. */
export function fileResources(file) {
    return { internalResources: file.subResources, externalResources: file.extResources };
}
/** The {@link SectionLoaderFn} over the parsed files `loadParsedFile` answers. */
export function sectionLoader(loadParsedFile) {
    return async (path, types) => {
        const file = await loadParsedFile(path);
        return { file, ...sectionOfType(file, path, types) };
    };
}
function sectionOfType(file, path, types) {
    const section = findResourceSection(file, path);
    if (!section) {
        const { filePath, subResourceId } = parseSubResourcePath(path);
        throw new Error(`${filePath} declares no sub-resource "${subResourceId}"`);
    }
    if (!types.has(section.type)) {
        throw new Error(`${path} has type ${section.type}, expected ${oneOf(types)}`);
    }
    return section;
}
/** `Theme`, or `one of FontFile, SystemFont, FontVariation`. */
function oneOf(types) {
    const names = [...types].join(', ');
    return types.size === 1 ? names : `one of ${names}`;
}
//# sourceMappingURL=resourceSection.js.map